// LWES Academy live chat v2 — Google-identified, threaded community chat
// backed by Firebase Firestore. Loaded as <script type="module"> only when
// chat is enabled and configured (see lwes-academy-landing.liquid).
//
// Identity: posting (root comments and replies) requires signing in with
// Google via Firebase Authentication. Name and photo come from the Google
// account — never typed in. Reading the chat stays open to anyone. The
// "Academy Team" badge is granted automatically when the signed-in user's
// Firebase uid matches one configured in chat_admin_uids.
//
// Threading: each root comment lives in `academyChatMessages`; replies live
// in a `replies` subcollection under that comment. `replyCount` on the
// parent is updated via a narrowly-scoped Firestore rule that only allows
// incrementing it by 1.
//
// Security model: every writer is signed in with Firebase Auth, so the
// Firestore rules verify request.auth and require the stored `uid` to match
// the caller — identity can't be spoofed. Rules allow public read + validated
// create (plus that one restricted replyCount increment); no edit/delete from
// the client. Moderation happens via the Firebase console.

const FIREBASE_SDK_VERSION = '10.12.2';

const RATE_LIMIT_MS = 9000;
const MESSAGE_MAX = 500;

const STORAGE_KEYS = {
  lastSent: 'lwesAcademyChatLastSent',
};

document.querySelectorAll('[data-lwes-academy-chat]').forEach((root) => {
  init(root);
});

async function init(root) {
  const config = readConfig(root);

  const state = {
    root,
    config,
    refs: {
      messages: root.querySelector('[data-lwes-chat-messages]'),
      empty: root.querySelector('[data-lwes-chat-empty]'),
      login: root.querySelector('[data-lwes-chat-login]'),
      form: root.querySelector('[data-lwes-chat-form]'),
      poster: root.querySelector('[data-lwes-chat-poster]'),
      message: root.querySelector('[data-lwes-chat-message]'),
      send: root.querySelector('[data-lwes-chat-send]'),
      status: root.querySelector('[data-lwes-chat-status]'),
    },
    db: null,
    firestore: null,
    auth: null,
    authMod: null,
    currentUser: null,
    emailAuthInFlight: false,
    comments: new Map(), // commentId -> { expanded, replies, unsubscribe }
    lastDocs: null,
  };

  try {
    const { initializeApp } = await import(
      `https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-app.js`
    );
    state.firestore = await import(
      `https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-firestore.js`
    );
    state.authMod = await import(
      `https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-auth.js`
    );
    const app = initializeApp(config.firebase);
    state.db = state.firestore.getFirestore(app);
    state.auth = state.authMod.getAuth(app);
  } catch (err) {
    showError(state.refs, 'Chat is temporarily unavailable. Please try again later.');
    console.error('[lwes-academy-chat] Firebase init failed', err);
    return;
  }

  listenForMessages(state);
  setupForm(state);
  setupAuthUI(state);
  renderComposerState(state);

  // Firebase Auth restores any existing session and notifies us on sign-in /
  // sign-out — the single source of truth for currentUser.
  state.authMod.onAuthStateChanged(state.auth, (user) => setCurrentUser(state, user));
}

function setCurrentUser(state, user) {
  if (requiresEmailVerification(user)) {
    state.currentUser = null;
    if (!state.emailAuthInFlight) {
      state.authMod.signOut(state.auth).catch((err) => {
        console.error('[lwes-academy-chat] unverified email sign-out failed', err);
      });
    }
  } else {
    state.currentUser = user ? toCurrentUser(user) : null;
  }
  renderComposerState(state);
  if (state.lastDocs) renderMessages(state, state.lastDocs);
}

function toCurrentUser(user) {
  return {
    uid: user.uid,
    name: user.displayName || 'Member',
    photoURL: user.photoURL || '',
  };
}

function readConfig(root) {
  const d = root.dataset;
  return {
    firebase: {
      apiKey: d.firebaseApiKey,
      authDomain: d.firebaseAuthDomain,
      projectId: d.firebaseProjectId,
      storageBucket: d.firebaseStorageBucket,
      messagingSenderId: d.firebaseMessagingSenderId,
      appId: d.firebaseAppId,
    },
    adminUids: parseIdList(d.adminUids),
    loginLabel: d.loginLabel || 'Continue with Google',
    replyLabel: d.replyLabel || 'Reply',
    repliesLabel: d.repliesLabel || 'replies',
    replyPlaceholder: d.replyPlaceholder || 'Write a reply...',
    deleteLabel: d.deleteLabel || 'Delete',
    logoutLabel: d.logoutLabel || 'Log out',
  };
}

function parseIdList(raw) {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(/[\n,]/)
      .map((id) => id.trim())
      .filter(Boolean)
  );
}

// ---- Sign-in via Firebase Authentication (Google, email/password) ----
// onAuthStateChanged (set up in init) is the single source of truth: every
// successful sign-in and sign-out flows through it to update currentUser. The
// only exception is email *sign-up*, where we push the freshly set display
// name immediately (see buildEmailAuthForm).

// Builds the sign-in choices into a container. The main composer gets the full
// panel (provider buttons + email form); reply composers get a compact one
// (provider buttons only — once signed in anywhere, every composer updates).
function setupAuthUI(state) {
  state.root.querySelectorAll('[data-lwes-chat-authui]').forEach((el) => {
    renderAuthPanel(state, el, false);
  });
}

function renderAuthPanel(state, container, compact) {
  container.replaceChildren();

  const providers = document.createElement('div');
  providers.className = 'la-chat__providers';
  providers.append(
    buildProviderButton('google', state.config.loginLabel, () => signInWithPopupProvider(state, googleProvider(state)))
  );
  container.appendChild(providers);

  if (!compact) {
    container.appendChild(buildDivider());
    container.appendChild(buildEmailAuthForm(state));
  }
}

function googleProvider(state) {
  return new state.authMod.GoogleAuthProvider();
}

function signInWithPopupProvider(state, provider) {
  state.authMod.signInWithPopup(state.auth, provider).catch((err) => {
    // The visitor closing the popup (or double-clicking) isn't worth surfacing.
    if (err && (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request')) {
      return;
    }
    console.error('[lwes-academy-chat] sign-in failed', err);
    window.alert('Could not sign in. Please try again.');
  });
}

function handleLogout(state) {
  state.authMod.signOut(state.auth).catch((err) => {
    console.error('[lwes-academy-chat] sign-out failed', err);
  });
}

function buildProviderButton(kind, label, onClick) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `la-chat__provider-btn la-chat__provider-btn--${kind}`;
  btn.appendChild(providerIcon(kind));
  const span = document.createElement('span');
  span.textContent = label;
  btn.appendChild(span);
  btn.addEventListener('click', onClick);
  return btn;
}

function buildDivider() {
  const div = document.createElement('div');
  div.className = 'la-chat__or';
  const span = document.createElement('span');
  span.textContent = 'or';
  div.appendChild(span);
  return div;
}

// Email/password sign-in with an inline toggle to account creation. New
// accounts collect a display name so the chat never shows a blank/"Member".
function buildEmailAuthForm(state) {
  const form = document.createElement('form');
  form.className = 'la-chat__email-form';
  form.noValidate = true;

  let mode = 'signin';

  const name = buildField('text', 'Your name', 'name');
  name.wrap.hidden = true;
  const email = buildField('email', 'Email', 'email');
  const password = buildField('password', 'Password', 'current-password');

  const submit = document.createElement('button');
  submit.type = 'submit';
  submit.className = 'la-btn la-btn--green la-chat__email-submit';
  submit.textContent = 'Sign in';

  const status = document.createElement('p');
  status.className = 'la-chat__status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'la-chat__login-link';
  toggle.textContent = 'New here? Create an account';
  toggle.addEventListener('click', () => {
    mode = mode === 'signin' ? 'signup' : 'signin';
    name.wrap.hidden = mode === 'signin';
    password.el.autocomplete = mode === 'signin' ? 'current-password' : 'new-password';
    submit.textContent = mode === 'signin' ? 'Sign in' : 'Create account';
    toggle.textContent = mode === 'signin' ? 'New here? Create an account' : 'Have an account? Sign in';
    setStatus(status, '', null);
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    setStatus(status, '', null);

    const emailVal = email.el.value.trim();
    const passVal = password.el.value;
    if (!emailVal || !passVal) {
      setStatus(status, 'Enter your email and password.', 'error');
      return;
    }

    setFormDisabled(form, true);
    state.emailAuthInFlight = true;
    try {
      if (mode === 'signup') {
        const nameVal = name.el.value.trim();
        if (!nameVal) {
          setStatus(status, 'Please enter your name.', 'error');
          return;
        }
        const cred = await state.authMod.createUserWithEmailAndPassword(state.auth, emailVal, passVal);
        await state.authMod.updateProfile(cred.user, { displayName: nameVal });
        await sendEmailVerificationAndSignOut(state, cred.user);
        setStatus(status, 'Account created. Check your email to verify it, then sign in.', 'success');
      } else {
        const cred = await state.authMod.signInWithEmailAndPassword(state.auth, emailVal, passVal);
        if (requiresEmailVerification(cred.user)) {
          await sendEmailVerificationAndSignOut(state, cred.user);
          setStatus(status, 'Please verify your email first. We sent a new verification email.', 'error');
          return;
        }
      }
      // onAuthStateChanged swaps the composer to the post form.
    } catch (err) {
      setStatus(status, emailAuthError(err), 'error');
    } finally {
      state.emailAuthInFlight = false;
      if (requiresEmailVerification(state.auth.currentUser)) {
        state.authMod.signOut(state.auth).catch((err) => {
          console.error('[lwes-academy-chat] unverified email sign-out failed', err);
        });
      }
      setFormDisabled(form, false);
    }
  });

  form.append(name.wrap, email.wrap, password.wrap, submit, status, toggle);
  return form;
}

async function sendEmailVerificationAndSignOut(state, user) {
  await state.authMod.sendEmailVerification(user);
  await state.authMod.signOut(state.auth);
}

function requiresEmailVerification(user) {
  return Boolean(user && isEmailPasswordUser(user) && !user.emailVerified);
}

function isEmailPasswordUser(user) {
  return Array.isArray(user.providerData) && user.providerData.some((provider) => provider.providerId === 'password');
}

function buildField(type, label, autocomplete) {
  const wrap = document.createElement('label');
  wrap.className = 'la-chat__field';
  const span = document.createElement('span');
  span.className = 'visually-hidden';
  span.textContent = label;
  const el = document.createElement('input');
  el.type = type;
  el.className = 'la-chat__email-input';
  el.placeholder = label;
  if (autocomplete) el.autocomplete = autocomplete;
  wrap.append(span, el);
  return { wrap, el };
}

function emailAuthError(err) {
  const code = err && err.code;
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/email-already-in-use':
      return 'That email already has an account — try signing in instead.';
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-credential':
      return 'Email or password is incorrect.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return 'Could not sign in. Please try again.';
  }
}

function providerIcon(kind) {
  return makeSvg('0 0 48 48', [
    ['M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z', '#FFC107'],
    ['M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z', '#FF3D00'],
    ['M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z', '#4CAF50'],
    ['M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z', '#1976D2'],
  ]);
}

function makeSvg(viewBox, paths) {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', viewBox);
  svg.setAttribute('aria-hidden', 'true');
  paths.forEach(([d, fill]) => {
    const path = document.createElementNS(NS, 'path');
    path.setAttribute('d', d);
    if (fill) path.setAttribute('fill', fill);
    svg.appendChild(path);
  });
  return svg;
}

// ---- Composer (login prompt vs. post form) ----

function renderComposerState(state) {
  state.root.querySelectorAll('[data-lwes-chat-composer]').forEach((composer) => {
    const loginEl = composer.querySelector('[data-lwes-chat-login]');
    const formEl = composer.querySelector('[data-lwes-chat-form]');
    const posterEl = composer.querySelector('[data-lwes-chat-poster]');

    if (state.currentUser) {
      loginEl.hidden = true;
      formEl.hidden = false;
      if (posterEl) renderPoster(state, posterEl);
    } else {
      loginEl.hidden = false;
      formEl.hidden = true;
    }
  });
}

function renderPoster(state, posterEl) {
  posterEl.replaceChildren();
  if (!state.currentUser) return;

  const avatar = createAvatar(state.currentUser.photoURL, state.currentUser.name, 'la-chat__avatar--sm');

  const name = document.createElement('span');
  name.className = 'la-chat__poster-name';
  name.textContent = state.currentUser.name;

  const logout = document.createElement('button');
  logout.type = 'button';
  logout.className = 'la-chat__logout';
  logout.textContent = state.config.logoutLabel;
  logout.addEventListener('click', () => handleLogout(state));

  posterEl.append(avatar, name, logout);
}

// ---- Real-time root listener ----

function listenForMessages(state) {
  const { collection, query, orderBy, limit, onSnapshot } = state.firestore;
  const q = query(collection(state.db, 'academyChatMessages'), orderBy('timestamp', 'desc'), limit(50));

  onSnapshot(
    q,
    (snapshot) => {
      const docs = [];
      snapshot.forEach((doc) => docs.push({ id: doc.id, data: doc.data() }));
      docs.reverse();
      renderMessages(state, docs);
    },
    (err) => {
      showError(state.refs, 'Unable to load messages right now.');
      console.error('[lwes-academy-chat] onSnapshot error', err);
    }
  );
}

// ---- Rendering (XSS-safe: textContent only, never innerHTML) ----

function renderMessages(state, docs) {
  state.lastDocs = docs;
  const { messages, empty } = state.refs;
  const wasNearBottom = isScrolledNearBottom(messages);

  messages.querySelectorAll('.la-chat__msg, .la-chat__error').forEach((el) => el.remove());

  if (docs.length === 0) {
    if (empty) empty.hidden = false;
    return;
  }
  if (empty) empty.hidden = true;

  docs.forEach(({ id, data }) => {
    messages.appendChild(renderMessage(state, id, data));
  });

  if (wasNearBottom) {
    messages.scrollTop = messages.scrollHeight;
  }
}

function renderMessage(state, id, msg) {
  const wrap = document.createElement('div');
  wrap.className = 'la-chat__msg';
  if (isAdminAuthor(state, msg)) wrap.classList.add('la-chat__msg--admin');
  if (isOwnMessage(state, msg)) wrap.classList.add('la-chat__msg--own');

  const avatar = createAvatar(msg.photoURL, msg.name);

  const bubble = document.createElement('div');
  bubble.className = 'la-chat__bubble';

  const authorLine = document.createElement('div');
  const author = document.createElement('span');
  author.className = 'la-chat__author';
  author.textContent = msg.name || 'Anonymous';
  authorLine.appendChild(author);

  if (isAdminAuthor(state, msg)) {
    authorLine.appendChild(createBadge());
  }

  const text = document.createElement('p');
  text.className = 'la-chat__text';
  text.textContent = msg.message || '';

  const meta = document.createElement('div');
  meta.className = 'la-chat__meta';

  const time = document.createElement('span');
  time.className = 'la-chat__time';
  time.textContent = formatTimestamp(msg.timestamp);
  meta.appendChild(time);

  const replyToggle = document.createElement('button');
  replyToggle.type = 'button';
  replyToggle.className = 'la-chat__reply-toggle';
  replyToggle.textContent = state.config.replyLabel;
  meta.appendChild(replyToggle);

  const replyCount = document.createElement('span');
  replyCount.className = 'la-chat__reply-count';
  const count = msg.replyCount || 0;
  replyCount.textContent = count > 0 ? `${count} ${state.config.repliesLabel}` : '';
  replyCount.hidden = count === 0;
  meta.appendChild(replyCount);

  if (isOwnMessage(state, msg)) {
    meta.appendChild(buildDeleteButton(state, () => deleteMessage(state, id, msg)));
  }

  bubble.append(authorLine, text, meta);

  const repliesEl = document.createElement('div');
  repliesEl.className = 'la-chat__replies';
  repliesEl.hidden = true;

  const repliesList = document.createElement('div');
  repliesList.className = 'la-chat__replies-list';
  repliesEl.appendChild(repliesList);
  repliesEl.appendChild(buildReplyComposer(state, id));

  bubble.appendChild(repliesEl);
  wrap.append(avatar, bubble);

  replyToggle.addEventListener('click', () => {
    const expanded = toggleReplies(state, id, repliesEl, repliesList);
    replyToggle.textContent = expanded ? 'Hide' : state.config.replyLabel;
  });

  // Restore expanded state across root re-renders.
  const existing = state.comments.get(id);
  if (existing && existing.expanded) {
    repliesEl.hidden = false;
    replyToggle.textContent = 'Hide';
    renderReplies(state, id, repliesList, existing.replies);
  }

  return wrap;
}

function renderReply(state, commentId, replyId, msg) {
  const wrap = document.createElement('div');
  wrap.className = 'la-chat__reply';
  if (isAdminAuthor(state, msg)) wrap.classList.add('la-chat__msg--admin');
  if (isOwnMessage(state, msg)) wrap.classList.add('la-chat__msg--own');

  const avatar = createAvatar(msg.photoURL, msg.name, 'la-chat__avatar--sm');

  const bubble = document.createElement('div');
  bubble.className = 'la-chat__bubble la-chat__bubble--sm';

  const authorLine = document.createElement('div');
  const author = document.createElement('span');
  author.className = 'la-chat__author';
  author.textContent = msg.name || 'Anonymous';
  authorLine.appendChild(author);

  if (isAdminAuthor(state, msg)) {
    authorLine.appendChild(createBadge());
  }

  const text = document.createElement('p');
  text.className = 'la-chat__text';
  text.textContent = msg.message || '';

  const meta = document.createElement('div');
  meta.className = 'la-chat__meta la-chat__meta--reply';

  const time = document.createElement('span');
  time.className = 'la-chat__time';
  time.textContent = formatTimestamp(msg.timestamp);
  meta.appendChild(time);

  if (isOwnMessage(state, msg)) {
    meta.appendChild(buildDeleteButton(state, () => deleteReply(state, commentId, replyId, msg)));
  }

  bubble.append(authorLine, text, meta);
  wrap.append(avatar, bubble);
  return wrap;
}

function renderReplies(state, commentId, repliesList, replies) {
  repliesList.replaceChildren();
  replies.forEach(({ id, data }) => {
    repliesList.appendChild(renderReply(state, commentId, id, data));
  });
}

// Small "Delete" link shown only on the current user's own message/reply. The
// Firestore rules also enforce that you can only delete your own (uid match).
function buildDeleteButton(state, onDelete) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'la-chat__delete';
  btn.textContent = state.config.deleteLabel;
  btn.addEventListener('click', onDelete);
  return btn;
}

async function deleteMessage(state, id, msg) {
  if (!state.currentUser || !isOwnMessage(state, msg)) return;
  if (!window.confirm('Delete this message? This cannot be undone.')) return;

  const { doc, deleteDoc } = state.firestore;
  try {
    // Drop the replies listener for this comment, if any. Any replies it had
    // become unreachable (same as deleting a post with comments) — they can be
    // cleared from the Firebase console if needed.
    const entry = state.comments.get(id);
    if (entry && entry.unsubscribe) entry.unsubscribe();
    state.comments.delete(id);
    await deleteDoc(doc(state.db, 'academyChatMessages', id));
    // The root onSnapshot removes the message from the list automatically.
  } catch (err) {
    console.error('[lwes-academy-chat] message delete failed', err);
    window.alert('Could not delete the message. Please try again.');
  }
}

async function deleteReply(state, commentId, replyId, msg) {
  if (!state.currentUser || !isOwnMessage(state, msg)) return;
  if (!window.confirm('Delete this reply? This cannot be undone.')) return;

  const { doc, deleteDoc, updateDoc, increment } = state.firestore;
  try {
    await deleteDoc(doc(state.db, 'academyChatMessages', commentId, 'replies', replyId));
    updateDoc(doc(state.db, 'academyChatMessages', commentId), { replyCount: increment(-1) }).catch((err) => {
      console.error('[lwes-academy-chat] reply count update failed', err);
    });
  } catch (err) {
    console.error('[lwes-academy-chat] reply delete failed', err);
    window.alert('Could not delete the reply. Please try again.');
  }
}

function createBadge() {
  const badge = document.createElement('span');
  badge.className = 'la-chat__badge';
  badge.textContent = 'Academy Team';
  return badge;
}

function createAvatar(photoURL, name, extraClass) {
  const avatar = document.createElement('div');
  avatar.className = extraClass ? `la-chat__avatar ${extraClass}` : 'la-chat__avatar';
  if (photoURL) {
    const img = document.createElement('img');
    img.src = photoURL;
    img.alt = '';
    img.loading = 'lazy';
    img.referrerPolicy = 'no-referrer';
    avatar.appendChild(img);
  } else {
    avatar.textContent = initials(name);
  }
  return avatar;
}

function isOwnMessage(state, msg) {
  return Boolean(msg.uid && state.currentUser && msg.uid === state.currentUser.uid);
}

// The "Academy Team" badge is derived at render time from the configured admin
// list (adminUids) keyed on the author's Firebase uid — never from a stored
// `isAdmin` flag, which any client could forge when writing a doc directly to
// Firestore with the public web key.
function isAdminAuthor(state, msg) {
  return Boolean(msg && msg.uid && state.config.adminUids.has(msg.uid));
}

function initials(name) {
  return (name || 'NP').trim().slice(0, 2).toUpperCase();
}

function formatTimestamp(ts) {
  if (!ts || typeof ts.toDate !== 'function') return 'Just now';
  return ts.toDate().toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isScrolledNearBottom(el) {
  return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
}

function showError(refs, msg) {
  if (refs.empty) refs.empty.hidden = true;
  let errorEl = refs.messages.querySelector('.la-chat__error');
  if (!errorEl) {
    errorEl = document.createElement('p');
    errorEl.className = 'la-chat__error';
    refs.messages.appendChild(errorEl);
  }
  errorEl.textContent = msg;
}

// ---- Threaded replies ----

function toggleReplies(state, commentId, repliesEl, repliesList) {
  let entry = state.comments.get(commentId);
  if (!entry) {
    entry = { expanded: false, replies: [], unsubscribe: null };
    state.comments.set(commentId, entry);
  }

  entry.expanded = !entry.expanded;
  repliesEl.hidden = !entry.expanded;

  if (!entry.unsubscribe) {
    const { collection, query, orderBy, onSnapshot } = state.firestore;
    const q = query(collection(state.db, 'academyChatMessages', commentId, 'replies'), orderBy('timestamp', 'asc'));
    entry.unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        entry.replies = [];
        snapshot.forEach((doc) => entry.replies.push({ id: doc.id, data: doc.data() }));
        if (entry.expanded) {
          renderReplies(state, commentId, repliesList, entry.replies);
        }
      },
      (err) => {
        console.error('[lwes-academy-chat] replies onSnapshot error', err);
      }
    );
  } else if (entry.expanded) {
    renderReplies(state, commentId, repliesList, entry.replies);
  }

  return entry.expanded;
}

// ---- Form submit / validation / rate limiting ----

function setupForm(state) {
  attachComposerSubmit(
    state,
    {
      form: state.refs.form,
      message: state.refs.message,
      status: state.refs.status,
    },
    null
  );
}

function buildReplyComposer(state, commentId) {
  const wrap = document.createElement('div');
  wrap.className = 'la-chat__composer la-chat__composer--reply';
  wrap.setAttribute('data-lwes-chat-composer', '');

  const loginEl = document.createElement('div');
  loginEl.className = 'la-chat__login la-chat__login--sm';
  loginEl.setAttribute('data-lwes-chat-login', '');

  // Reply composers show a compact panel (provider buttons only). Once signed
  // in anywhere — including the main composer's email form — every composer
  // updates via onAuthStateChanged.
  const authui = document.createElement('div');
  authui.className = 'la-chat__authui';
  renderAuthPanel(state, authui, true);
  loginEl.appendChild(authui);

  const formEl = document.createElement('form');
  formEl.className = 'la-chat__form la-chat__form--reply';
  formEl.noValidate = true;
  formEl.setAttribute('data-lwes-chat-form', '');

  const posterEl = document.createElement('div');
  posterEl.className = 'la-chat__poster la-chat__poster--sm';
  posterEl.setAttribute('data-lwes-chat-poster', '');

  const row = document.createElement('div');
  row.className = 'la-chat__row la-chat__row--message';

  const textarea = document.createElement('textarea');
  textarea.className = 'la-chat__message la-chat__reply-input';
  textarea.rows = 1;
  textarea.maxLength = MESSAGE_MAX;
  textarea.placeholder = state.config.replyPlaceholder;
  textarea.required = true;

  const sendBtn = document.createElement('button');
  sendBtn.type = 'submit';
  sendBtn.className = 'la-btn la-btn--green la-chat__send';
  sendBtn.textContent = state.refs.send.textContent;

  row.append(textarea, sendBtn);

  const status = document.createElement('p');
  status.className = 'la-chat__status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');

  formEl.append(posterEl, row, status);
  wrap.append(loginEl, formEl);

  attachComposerSubmit(state, { form: formEl, message: textarea, status }, commentId);

  if (state.currentUser) {
    loginEl.hidden = true;
    formEl.hidden = false;
    renderPoster(state, posterEl);
  } else {
    loginEl.hidden = false;
    formEl.hidden = true;
  }

  return wrap;
}

function attachComposerSubmit(state, refs, commentId) {
  refs.form.addEventListener('submit', async (e) => {
    e.preventDefault();
    setStatus(refs.status, '', null);

    if (!state.currentUser) {
      setStatus(refs.status, 'Please sign in first.', 'error');
      return;
    }

    const message = (refs.message.value || '').trim().slice(0, MESSAGE_MAX);
    if (!message) {
      setStatus(refs.status, 'Please enter a message.', 'error');
      return;
    }

    const remaining = rateLimitRemaining();
    if (remaining > 0) {
      setStatus(refs.status, `Please wait ${remaining}s before sending another message.`, 'error');
      return;
    }

    const { collection, addDoc, doc, updateDoc, increment, serverTimestamp } = state.firestore;
    const payload = {
      name: state.currentUser.name,
      message,
      photoURL: state.currentUser.photoURL,
      uid: state.currentUser.uid,
      timestamp: serverTimestamp(),
    };

    setFormDisabled(refs.form, true);
    try {
      const target = commentId
        ? collection(state.db, 'academyChatMessages', commentId, 'replies')
        : collection(state.db, 'academyChatMessages');
      await addDoc(target, payload);

      if (commentId) {
        const parentRef = doc(state.db, 'academyChatMessages', commentId);
        updateDoc(parentRef, { replyCount: increment(1) }).catch((err) => {
          console.error('[lwes-academy-chat] reply count update failed', err);
        });
      }

      refs.message.value = '';
      localStorage.setItem(STORAGE_KEYS.lastSent, String(Date.now()));
      setStatus(refs.status, 'Sent!', 'success');
    } catch (err) {
      console.error('[lwes-academy-chat] addDoc failed', err);
      setStatus(refs.status, 'Could not send your message. Please try again.', 'error');
    } finally {
      setFormDisabled(refs.form, false);
      refs.message.focus();
    }
  });
}

function rateLimitRemaining() {
  const last = Number(localStorage.getItem(STORAGE_KEYS.lastSent) || 0);
  const elapsed = Date.now() - last;
  return elapsed >= RATE_LIMIT_MS ? 0 : Math.ceil((RATE_LIMIT_MS - elapsed) / 1000);
}

function setFormDisabled(formEl, disabled) {
  formEl.querySelectorAll('input, textarea, button').forEach((el) => {
    el.disabled = disabled;
  });
}

function setStatus(statusEl, msg, type) {
  if (!statusEl) return;
  statusEl.textContent = msg;
  statusEl.classList.toggle('is-error', type === 'error');
  statusEl.classList.toggle('is-success', type === 'success');
}
